import { http, HttpResponse } from 'msw';
import type { LayerItem, UsageStageInfo, BuildingColorModeInfo, LegendStep, BuildingColorMode } from './types';

// ─── Étapes métier (issues du recueil de besoins) ────────────────────────────

export const USAGE_STAGES: UsageStageInfo[] = [
  {
    id: 'consumption',
    label: 'Conso & Prospects',
    description: 'Identifier le potentiel de consommation et les prospects',
    icon: 'bolt',
  },
  {
    id: 'resources',
    label: 'Ressources énergétiques',
    description: 'Identifier les ressources mobilisables pour alimenter le réseau',
    icon: 'eco',
  },
  {
    id: 'network-study',
    label: 'Étude de tracé',
    description: 'Analyser les contraintes techniques de passage du réseau',
    icon: 'route',
  },
  {
    id: 'future-development',
    label: 'Développement futur',
    description: 'Anticiper les évolutions du territoire',
    icon: 'trending_up',
  },
  {
    id: 'ancillary',
    label: 'Usages annexes',
    description: 'Contextualiser une étude ou une présentation',
    icon: 'more_horiz',
  },
];

// ─── Catalogue des couches (données réalistes, français) ────────────────────

export const MOCK_LAYERS: LayerItem[] = [
  // 1. Conso & Prospects
  { id: 'pdl-grdf', label: 'PDL GRDF (points de livraison gaz)', stages: ['consumption'], isEngieInternal: false, visible: true, isFavorite: true },
  { id: 'pdl-enedis', label: 'PDL Enedis (points de livraison électricité)', stages: ['consumption'], isEngieInternal: false, visible: false, isFavorite: false },
  { id: 'pdl-reseaux-chaud-froid', label: 'PDL Réseaux de Chaleur/Froid', stages: ['consumption'], isEngieInternal: true, visible: true, isFavorite: true },
  { id: 'postes-livraison-gaz', label: 'Postes de livraison gaz', stages: ['consumption'], isEngieInternal: false, visible: false, isFavorite: false },
  { id: 'postes-sources-enedis', label: 'Postes sources ENEDIS', stages: ['consumption'], isEngieInternal: false, visible: false, isFavorite: false },
  { id: 'canalisations-engie', label: 'Canalisations réseaux existants ENGIE', stages: ['consumption', 'network-study'], isEngieInternal: true, visible: true, isFavorite: false },
  { id: 'canalisations-non-engie', label: 'Canalisations réseaux existants non ENGIE', stages: ['consumption', 'network-study'], isEngieInternal: false, visible: false, isFavorite: false },

  // 2. Ressources énergétiques
  { id: 'step', label: "Stations d'épuration", stages: ['resources'], isEngieInternal: false, visible: false, isFavorite: false },
  { id: 'uve', label: "UVE - Unités de valorisation énergétique", stages: ['resources'], isEngieInternal: false, visible: false, isFavorite: false },
  { id: 'datacenters', label: 'Datacenters', stages: ['resources'], isEngieInternal: false, visible: false, isFavorite: false },
  { id: 'entrepots-frigo', label: 'Entrepôts frigorifiques', stages: ['resources'], isEngieInternal: false, visible: false, isFavorite: false },
  { id: 'centrales', label: 'Centrales', stages: ['resources'], isEngieInternal: true, visible: false, isFavorite: false },
  { id: 'geothermie', label: 'Géothermies sur sonde', stages: ['resources'], isEngieInternal: false, visible: false, isFavorite: false },
  { id: 'icpe', label: 'ICPE', stages: ['resources'], isEngieInternal: false, visible: false, isFavorite: false },
  { id: 'dry-coolers', label: 'Groupes froids / Dry coolers', stages: ['resources'], isEngieInternal: false, visible: false, isFavorite: false },

  // 3. Étude de tracé
  { id: 'reseaux-grdf', label: 'Réseaux GRDF', stages: ['network-study'], isEngieInternal: false, visible: false, isFavorite: false },
  { id: 'reseaux-enedis', label: 'Réseaux ENEDIS', stages: ['network-study'], isEngieInternal: false, visible: false, isFavorite: false },
  { id: 'routes', label: 'Routes', stages: ['network-study'], isEngieInternal: false, visible: true, isFavorite: false },
  { id: 'zones-archeo', label: 'Zones archéologiques', stages: ['network-study'], isEngieInternal: false, visible: false, isFavorite: false },
  { id: 'rivieres', label: 'Rivières et cours d\'eau', stages: ['network-study'], isEngieInternal: false, visible: false, isFavorite: false },

  // 4. Développement futur
  { id: 'projets-immobiliers', label: 'Projets immobiliers', stages: ['future-development'], isEngieInternal: false, visible: false, isFavorite: false },
  { id: 'zones-amenagement', label: "Zones d'aménagement", stages: ['future-development'], isEngieInternal: false, visible: false, isFavorite: false },
  { id: 'reseaux-construction', label: 'Réseaux en construction', stages: ['future-development'], isEngieInternal: true, visible: false, isFavorite: false },
  { id: 'zones-reseaux-construction', label: 'Zones de réseaux en construction', stages: ['future-development'], isEngieInternal: true, visible: false, isFavorite: false },

  // 5. Usages annexes
  { id: 'contours-administratifs', label: 'Contours administratifs', stages: ['ancillary'], isEngieInternal: false, visible: true, isFavorite: false },
  { id: 'perimetres-contrat', label: 'Périmètres de contrat', stages: ['ancillary'], isEngieInternal: true, visible: false, isFavorite: false },
  { id: 'zones-classement', label: 'Zones de classement', stages: ['ancillary'], isEngieInternal: false, visible: false, isFavorite: false },
  { id: 'parcelles', label: 'Parcelles cadastrales', stages: ['ancillary'], isEngieInternal: false, visible: true, isFavorite: false },
  { id: 'zac', label: "ZAC (zones d'aménagement concerté)", stages: ['ancillary'], isEngieInternal: false, visible: false, isFavorite: false },
  { id: 'quartiers-prioritaires', label: 'Quartiers prioritaires', stages: ['ancillary'], isEngieInternal: false, visible: false, isFavorite: false },
  { id: 'zones-climatiques', label: 'Zones climatiques', stages: ['ancillary'], isEngieInternal: false, visible: false, isFavorite: false },
  { id: 'stations-meteo', label: 'Stations météo', stages: ['ancillary'], isEngieInternal: false, visible: false, isFavorite: false },
  { id: 'servitudes-aeronautiques', label: 'Servitudes aéronautiques', stages: ['ancillary'], isEngieInternal: false, visible: false, isFavorite: false },
];

// ─── Couche "Bâtiments" — modes de colorisation & légendes ──────────────────

export const BUILDING_COLOR_MODES: BuildingColorModeInfo[] = [
  { id: 'heat', label: 'Conso. chaud' },
  { id: 'cold', label: 'Conso. froid' },
  { id: 'dpe', label: 'DPE' },
];

export const BUILDING_LEGENDS: Record<BuildingColorMode, LegendStep[]> = {
  heat: [
    { label: '≤ 300 MWhu', color: '#ccd4da' },
    { label: '≤ 600 MWhu', color: '#f97316' },
    { label: '≤ 1000 MWhu', color: '#eab308' },
    { label: '+ 1000 MWhu', color: '#10b981' },
  ],
  cold: [
    { label: '≤ 15 MWhu', color: '#ccd4da' },
    { label: '≤ 30 MWhu', color: '#38bdf8' },
    { label: '≤ 60 MWhu', color: '#0ea5e9' },
    { label: '+ 60 MWhu', color: '#0369a1' },
  ],
  dpe: [
    { label: 'A - B', color: '#10b981' },
    { label: 'C - D', color: '#eab308' },
    { label: 'E - F', color: '#f97316' },
    { label: 'G', color: '#dc2626' },
  ],
};

// ─── MSW handlers ────────────────────────────────────────────────────────────

export const handlers = [
  http.get('/api/urban-map/layers', () => {
    return HttpResponse.json(MOCK_LAYERS);
  }),
];
